import React, { useState } from "react";
import { CodedProperty } from "sprint-common-web";

import { AncillaryContentSubtype } from "../../../common/types/AncillaryContentType";
import { AncillaryItem } from "../listing/AncillaryItem";
import { UpdatedAncillaryItem } from "../listing/edit-ancillary-form/EditAncillaryForm";
import { ManageAncillaryContext } from "./ManageAncillaryContext";

interface ManageAncillaryProviderProps {
    children: React.ReactNode;
    ancillaryContentType: AncillaryContentSubtype;
}

export const ManageAncillaryProvider: React.FC<ManageAncillaryProviderProps> = (
    props: ManageAncillaryProviderProps
) => {
    const { children, ancillaryContentType } = props;

    const [ancillaryItems, setAncillaryItems] = useState<AncillaryItem[]>([]);
    const [ancillariesToMerge, setAncillariesToMerge] = useState<AncillaryItem[]>([]);

    const addAncillaryItem = (ancillary: AncillaryItem) => {
        setAncillaryItems((prevState) => [
            ...prevState,
            {
                id: ancillary.id,
                title: ancillary.title,
                distributor: ancillary.distributor,
                airlines: getAirlines(ancillary.airlines),
                orphaned: ancillary.orphaned,
                source: ancillary.source,
                versionType: {
                    id: ancillary.versionType.id,
                    name: ancillary.versionType.name
                },
                isModified: true,
                buzz: ancillary.buzz
            }
        ]);
    };

    const updateAncillaryItem = (updatedAncillary: UpdatedAncillaryItem) => {
        const currentAncillaryList = getUpdatedAncillaryList(updatedAncillary, ancillaryItems);
        setAncillaryItems(currentAncillaryList);
    };

    const addAncillaryToMerge = (item: AncillaryItem) => {
        setAncillariesToMerge((prevState: AncillaryItem[]) => [...prevState, item]);
    };

    const markAncillaryAsMerged = (ancillaryId: string) => {
        const ancillaryList = [...ancillaryItems];

        const itemIndex = ancillaryList.findIndex((ancillary: AncillaryItem) => ancillary.id === ancillaryId);

        if (itemIndex < 0) return;

        ancillaryList[itemIndex].merged = true;

        setAncillaryItems(ancillaryList);
    };

    const clearAncillariesToMerge = () => {
        setAncillariesToMerge([]);
    };

    const removeAncillaryToMerge = (item: AncillaryItem) => {
        setAncillariesToMerge((prevState: AncillaryItem[]) => {
            return prevState.filter((ancillary: AncillaryItem) => ancillary.id !== item.id);
        });
    };

    return (
        <ManageAncillaryContext.Provider
            value={{
                ancillaryContentType,
                ancillaryItems,
                ancillariesToMerge,
                setAncillaryItems,
                addAncillaryItem,
                updateAncillaryItem,
                addAncillaryToMerge,
                removeAncillaryToMerge,
                markAncillaryAsMerged,
                clearAncillariesToMerge
            }}
        >
            {children}
        </ManageAncillaryContext.Provider>
    );
};

const getAirlines = (airlines: CodedProperty[]): CodedProperty[] => {
    return airlines.map((airline) => ({
        id: airline.id,
        name: airline.name,
        code: airline.code
    }));
};

const getUpdatedAncillaryList = (
    updatedAncillary: UpdatedAncillaryItem,
    ancillaryList: AncillaryItem[]
): AncillaryItem[] => {
    return ancillaryList.map((ancillary) =>
        ancillary.id === updatedAncillary.id
            ? {
                  ...ancillary,
                  title: updatedAncillary.data.name,
                  isModified: true,
                  versionType: {
                      id: updatedAncillary.data.versionType.id,
                      name: updatedAncillary.data.versionType.name
                  },
                  distributor: updatedAncillary.data.distributor,
                  airlines: getAirlines(updatedAncillary.data.airlines)
              }
            : ancillary
    );
};
